import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import { colors } from '../../theme/colors';
import { roundToTickSize } from '../../utils/tickSizeUtils';
import FormInput from '../shared/FormInput';

/**
 * Modal for editing or canceling an order
 * Allows updating price for stop, limit, or stop limit orders
 * @param {number} initialPrice - Optional initial price to pre-fill (e.g., from drag)
 */
const OrderEditModal = ({ show, onHide, onUpdatePrice, onCancelOrder, order, ticker, initialPrice = null }) => {
  const [price, setPrice] = useState('');
  const [isPriceFocused, setIsPriceFocused] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);

  // Initialize price from order when modal opens, or use initialPrice if provided (e.g., from drag)
  useEffect(() => {
    if (show && order) {
      // If initialPrice is provided (e.g., from drag), use it; otherwise use order's current price
      if (initialPrice !== null && typeof initialPrice === 'number' && isFinite(initialPrice)) {
        setPrice(initialPrice.toFixed(2));
      } else {
        const currentPrice = order.LimitPrice || order.StopPrice;
        if (currentPrice) {
          const priceNum = parseFloat(currentPrice);
          setPrice(priceNum.toFixed(2));
        } else {
          setPrice('');
        }
      }
    }
  }, [show, order, initialPrice]);

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setPrice('');
      setIsPriceFocused(false);
      setIsUpdating(false);
      setIsCanceling(false);
    }
  }, [show]);

  // Don't render anything if modal is not visible
  if (!show) {
    return null;
  }

  // If no order provided, show error message (shouldn't happen during normal usage)
  if (!order) {
    console.warn('[OrderEditModal] No order provided but show is true');
    return (
      <Modal show={show} onHide={onHide} centered size="sm">
        <Modal.Body style={{ backgroundColor: colors.bgPrimary, color: colors.text }}>
          <p>Error: No order data available. Please try again.</p>
          <Button onClick={onHide}>Close</Button>
        </Modal.Body>
      </Modal>
    );
  }

  const leg = order?.Legs?.[0] || {};
  const currentQuantity = leg.QuantityOrdered || order.Quantity || '';
  const side = leg.BuyOrSell || '';
  const orderType = order.OrderType || '';
  const timeInForce =
    order.Duration ||
    order.TimeInForce?.Duration ||
    order.TimeInForce?.duration ||
    (typeof order.TimeInForce === 'string' ? order.TimeInForce : '') ||
    '';
  const currentPriceRaw = order.LimitPrice || order.StopPrice;
  const currentPrice = currentPriceRaw ? parseFloat(currentPriceRaw) : null;
  const symbol = leg.Symbol || order.Symbol || '';
  
  // Helper function to format currency
  const formatCurrency = (value) => {
    if (!value && value !== 0) return 'Market';
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(num)) return 'Market';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };
  
  // Determine if order has a price that can be updated
  const hasPrice = order.LimitPrice || order.StopPrice;
  const isStopLimit = order.LimitPrice && order.StopPrice;
  const canUpdatePrice = hasPrice && (orderType === 'Limit' || orderType === 'StopMarket' || orderType === 'StopLimit');

  const handleUpdatePrice = async () => {
    if (!canUpdatePrice || !price || isUpdating) return;

    const priceNum = parseFloat(price);
    if (isNaN(priceNum) || priceNum <= 0) {
      return;
    }

    setIsUpdating(true);
    try {
      // Close immediately so the UI is responsive while async update runs.
      // Success/failure banners are shown by the parent handlers.
      onHide();

      // Round to tick size
      const roundedPrice = roundToTickSize(priceNum, ticker).toFixed(2);
      
      // Determine which price to update
      let updatePayload = {};
      const limitN = parseFloat(order.LimitPrice);
      const stopN = parseFloat(order.StopPrice);
      const hasLimit = Number.isFinite(limitN) && limitN > 0;
      const hasStop = Number.isFinite(stopN) && stopN > 0;
      if (hasLimit && hasStop) {
        // Stop limit: prefer updating StopPrice when editing from a stop-line drag
        // (initialPrice); otherwise update LimitPrice from the modal default path.
        if (initialPrice != null && Math.abs(initialPrice - stopN) < Math.abs(initialPrice - limitN)) {
          updatePayload = { StopPrice: roundedPrice };
        } else {
          updatePayload = { LimitPrice: roundedPrice };
        }
      } else if (hasLimit) {
        updatePayload = { LimitPrice: roundedPrice };
      } else if (hasStop) {
        updatePayload = { StopPrice: roundedPrice };
      }

      await onUpdatePrice(order.OrderID, updatePayload);
    } catch (error) {
      console.error('Failed to update order price:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelOrder = async () => {
    if (isCanceling) return;
    
    setIsCanceling(true);
    try {
      await onCancelOrder(order.OrderID);
      onHide();
    } catch (error) {
      console.error('Failed to cancel order:', error);
    } finally {
      setIsCanceling(false);
    }
  };

  // Parse formatted price (removes $ and commas)
  const parseFormattedPrice = (formattedValue) => {
    if (!formattedValue || formattedValue === '') return '';
    return formattedValue.replace(/[$,]/g, '');
  };

  const handlePriceChange = (e) => {
    const inputValue = parseFormattedPrice(e.target.value);
    setPrice(inputValue);
  };

  const handlePriceFocus = () => {
    setIsPriceFocused(true);
  };

  const handlePriceBlur = () => {
    setIsPriceFocused(false);
    if (price) {
      const rounded = roundToTickSize(parseFloat(price), ticker);
      setPrice(rounded.toFixed(2));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && canUpdatePrice && price) {
      handleUpdatePrice();
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="sm">
      <Modal.Header 
        style={{ 
          backgroundColor: colors.bgSecondary, 
          color: colors.text,
          borderBottom: `1px solid ${colors.border}`,
          position: 'relative'
        }}
      >
        <Modal.Title style={{ color: colors.text, fontSize: '1rem', fontWeight: 600 }}>
          Edit Order
        </Modal.Title>
        <button
          onClick={onHide}
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'transparent',
            border: 'none',
            color: colors.text,
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '4px',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colors.bgPrimary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          aria-label="Close"
        >
          <X size={20} />
        </button>
      </Modal.Header>
      
      <Modal.Body style={{ backgroundColor: colors.bgPrimary, color: colors.text }}>
        <div style={{ lineHeight: 1.6 }}>
          {/* Order Info */}
          <div 
            style={{ 
              backgroundColor: colors.bgSecondary, 
              padding: '0.75rem', 
              borderRadius: '4px',
              fontSize: '0.9rem',
              marginBottom: '1rem'
            }}
          >
            {order.OrderID && (
              <div style={{ fontSize: '0.85rem', color: colors.textMuted, marginBottom: '0.5rem' }}>
                Order ID: {order.OrderID}
              </div>
            )}
            <div><strong>Symbol:</strong> {symbol}</div>
            <div><strong>Side:</strong> {side}</div>
            <div><strong>Quantity:</strong> {currentQuantity}</div>
            <div><strong>Type:</strong> {orderType}</div>
            <div><strong>Time in force:</strong> {timeInForce || '—'}</div>
            <div><strong>Price:</strong> {formatCurrency(currentPrice)}</div>
          </div>

          {/* Price Input */}
          {canUpdatePrice && (
            <div style={{ marginBottom: '1rem' }}>
              <label 
                htmlFor="order-price-input"
                style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  color: colors.text
                }}
              >
                New Price
              </label>
              <FormInput
                id="order-price-input"
                type="text"
                value={price}
                onChange={handlePriceChange}
                onFocus={handlePriceFocus}
                onBlur={handlePriceBlur}
                onKeyDown={handleKeyDown}
                placeholder={currentPrice ? currentPrice.toFixed(2) : ''}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  fontSize: '1rem',
                  backgroundColor: colors.bgSecondary,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '4px',
                  color: colors.text,
                }}
              />
            </div>
          )}

          {!canUpdatePrice && (
            <div style={{ 
              padding: '0.75rem',
              backgroundColor: colors.bgSecondary,
              borderRadius: '4px',
              marginBottom: '1rem',
              fontSize: '0.9rem',
              color: colors.textMuted
            }}>
              This order type cannot be updated. You can cancel it below.
            </div>
          )}
        </div>
      </Modal.Body>
      
      <Modal.Footer style={{ backgroundColor: colors.bgSecondary, borderTop: `1px solid ${colors.border}` }}>
        <div style={{ display: 'flex', gap: '0.5rem', width: '100%', justifyContent: 'space-between' }}>
          <Button 
            variant="danger" 
            onClick={handleCancelOrder} 
            disabled={isCanceling}
            size="sm"
            style={{ 
              minWidth: '100px'
            }}
          >
            {isCanceling ? 'Canceling...' : 'Cancel Order'}
          </Button>
          {canUpdatePrice && (
            <Button 
              variant="primary" 
              onClick={handleUpdatePrice} 
              disabled={isUpdating || !price || parseFloat(price) <= 0}
              size="sm"
              style={{ minWidth: '100px' }}
            >
              {isUpdating ? 'Updating...' : 'Update Order'}
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default OrderEditModal;

